import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FileText, Plus, Calendar, User } from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import CaseActionsDropdown from "@/components/CaseActionsDropdown";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CasesPage = () => {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    case_name: "",
    patient_name: "",
    date_of_injury: ""
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchCases();
  }, []);

  const fetchCases = async () => {
    try {
      const response = await axios.get(`${API}/cases`);
      // Filter out archived cases - they should only show in Archives page
      const activeCases = response.data.filter(c => c.archive_status !== 'archived');
      setCases(activeCases);
    } catch (error) {
      console.error("Error fetching cases:", error);
      toast.error("Failed to load cases");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCase = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/cases`, formData);
      toast.success("Case created successfully");
      setDialogOpen(false);
      setFormData({ case_name: "", patient_name: "", date_of_injury: "" });
      fetchCases();
    } catch (error) {
      console.error("Error creating case:", error);
      toast.error("Failed to create case");
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-12">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-5xl font-bold mb-3" style={{ color: '#FFFFFF' }} data-testid="page-title">
                Chronology & Specials Generator
              </h1>
              <p className="text-lg" style={{ color: '#F5F7FA' }}>
                Professional medical-legal analysis for personal injury demands
              </p>
            </div>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  size="lg" 
                  className="px-8 h-12 text-base font-medium"
                  style={{ backgroundColor: '#245451', color: '#FFFFFF' }}
                  data-testid="create-case-btn"
                >
                  <Plus className="mr-2 h-5 w-5" />
                  New Case
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px] glass-card border-0">
                <DialogHeader>
                  <DialogTitle style={{ color: '#245451' }}>Create New Case</DialogTitle>
                  <DialogDescription style={{ color: '#64748B' }}>
                    Enter the case details to begin medical record analysis
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateCase} className="space-y-6 pt-4">
                  <div className="space-y-2">
                    <Label htmlFor="case_name">Case Name</Label>
                    <Input
                      id="case_name"
                      data-testid="case-name-input"
                      placeholder="e.g., Smith v. Jones"
                      value={formData.case_name}
                      onChange={(e) => setFormData({ ...formData, case_name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="patient_name">Patient Name</Label>
                    <Input
                      id="patient_name"
                      data-testid="patient-name-input"
                      placeholder="e.g., John Smith"
                      value={formData.patient_name}
                      onChange={(e) => setFormData({ ...formData, patient_name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="date_of_injury">Date of Injury</Label>
                    <Input
                      id="date_of_injury"
                      data-testid="date-injury-input"
                      type="date"
                      value={formData.date_of_injury}
                      onChange={(e) => setFormData({ ...formData, date_of_injury: e.target.value })}
                      required
                    />
                  </div>
                  <div className="flex justify-end space-x-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setDialogOpen(false)}
                      style={{ borderColor: '#E2E8F0', color: '#64748B' }}
                    >
                      Cancel
                    </Button>
                    <Button 
                      type="submit" 
                      data-testid="submit-case-btn"
                      style={{ backgroundColor: '#245451' }}
                    >
                      Create Case
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Cases Grid */}
        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-slate-600">Loading cases...</p>
          </div>
        ) : cases.length === 0 ? (
          <Card className="border-dashed border-2 glass-card">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <FileText className="h-16 w-16 mb-4" style={{ color: '#84a2ac' }} />
              <h3 className="text-xl font-semibold mb-2" style={{ color: '#1A1A1A' }}>No cases yet</h3>
              <p className="mb-6" style={{ color: '#406962' }}>Create your first case to get started</p>
              <Button 
                onClick={() => setDialogOpen(true)}
                data-testid="create-first-case-btn"
                style={{ backgroundColor: '#245451' }}
              >
                <Plus className="mr-2 h-4 w-4" />
                Create First Case
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="glass-card rounded-lg overflow-hidden" data-testid="cases-list">
            {/* Table Header */}
            <div 
              className="grid grid-cols-12 gap-4 px-6 py-4 font-semibold text-sm border-b"
              style={{ 
                backgroundColor: 'rgba(36, 84, 81, 0.08)',
                borderColor: 'rgba(255, 255, 255, 0.2)',
                color: '#245451'
              }}
            >
              <div className="col-span-3">Case Name</div>
              <div className="col-span-2">Client Name</div>
              <div className="col-span-2">Date of Loss</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-2">Created Date</div>
              <div className="col-span-1 text-right">Actions</div>
            </div>

            {/* Table Rows */}
            <div className="divide-y" style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}>
              {cases.map((caseItem) => (
                <div
                  key={caseItem.id}
                  className="grid grid-cols-12 gap-4 px-6 py-4 hover:bg-white/20 transition-colors cursor-pointer group"
                  onClick={() => navigate(`/case/${caseItem.id}`)}
                  data-testid={`case-row-${caseItem.id}`}
                >
                  {/* Case Name */}
                  <div className="col-span-3 flex items-center">
                    <div className="flex flex-col">
                      <span className="font-medium" style={{ color: '#245451' }}>
                        {caseItem.case_name}
                      </span>
                    </div>
                  </div>

                  {/* Client Name */}
                  <div className="col-span-2 flex items-center">
                    <div className="flex items-center" style={{ color: '#64748B' }}>
                      <User className="h-4 w-4 mr-2" style={{ color: '#84a2ac' }} />
                      <span className="text-sm">{caseItem.patient_name}</span>
                    </div>
                  </div>

                  {/* Date of Loss */}
                  <div className="col-span-2 flex items-center">
                    <div className="flex items-center" style={{ color: '#64748B' }}>
                      <Calendar className="h-4 w-4 mr-2" style={{ color: '#84a2ac' }} />
                      <span className="text-sm">
                        {new Date(caseItem.date_of_injury).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  {/* Status */}
                  <div className="col-span-2 flex items-center">
                    <span 
                      className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
                      style={{
                        backgroundColor: caseItem.status === 'completed' ? '#D1F4E0' :
                                       caseItem.status === 'processing' ? '#FEF3C7' : '#F1F5F9',
                        color: caseItem.status === 'completed' ? '#245451' :
                              caseItem.status === 'processing' ? '#854D0E' : '#475569'
                      }}
                    >
                      {caseItem.status || 'draft'}
                    </span>
                  </div>

                  {/* Created Date with Time */}
                  <div className="col-span-2 flex items-center">
                    <div className="flex flex-col">
                      <span className="text-sm" style={{ color: '#84a2ac' }}>
                        {new Date(caseItem.created_at).toLocaleDateString()}
                      </span>
                      <span className="text-xs" style={{ color: '#94a3b8' }}>
                        {new Date(caseItem.created_at).toLocaleTimeString('en-US', { 
                          hour: 'numeric', 
                          minute: '2-digit',
                          hour12: true 
                        })}
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="col-span-1 flex items-center justify-end" onClick={(e) => e.stopPropagation()}>
                    <CaseActionsDropdown caseItem={caseItem} onUpdate={fetchCases} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default CasesPage;
